// Generic scroll-enter/leave signal for any element, reusable across blocks: add
// data-scroll-reveal (and an initial data-scroll-state="leave", rendered server-side by the
// consuming view) to an element and this toggles data-scroll-state="entered"/"leave" as it
// crosses into/out of the viewport - CSS then owns whatever transition that state should drive
// (fade, color change, transform, etc.), the same data-attribute-driven split used by
// image-caption.ts, just generalized instead of tied to one component's own open/closed state.
//
// Progressive enhancement: the view renders data-scroll-state="leave" directly in the initial
// HTML, so CSS's default (un-stated) look must be the REVEALED/"entered" one - a no-JS visitor,
// or one whose script hasn't run yet, sees the correct final content rather than something only
// this module can unlock. If IntersectionObserver isn't available, this module removes that
// initial "leave" state outright (see the fallback branch below) so those elements fall back to
// the same default revealed look, rather than being left stuck on their server-rendered starting
// state forever with nothing left to ever move them to "entered".
//
// Default behavior repeats: toggles back to "leave" every time the element scrolls out of view,
// then back to "entered" on re-entry, so effects replay each time the element crosses the
// viewport edge in either direction. Add data-scroll-reveal-once to an element to instead fire
// once and stay "entered" forever after the first entry (unobserved from then on) - use this for
// effects that shouldn't replay, e.g. a heading fade-in that would look distracting flipping back
// to its pre-reveal state every time the user scrolls back past it.
//
// Uses IntersectionObserver, not a scroll event listener - intersection changes are computed off
// the main thread's scroll-handling path, so there's no per-scroll-pixel work to throttle/debounce
// or schedule via requestAnimationFrame here; the only per-frame animation work (the actual color/
// opacity/transform change) is a CSS transition driven off the resulting data-* attribute, which
// the compositor handles on its own.
//
// One IntersectionObserver instance is shared across every data-scroll-reveal element rather than
// one-per-element, grouped by threshold (the one option IntersectionObserver fixes per-instance) -
// cuts down on redundant observer instances/callbacks if this ends up used on many elements per
// page. Each element's own "once" flag is read from its dataset at callback time, not captured
// into a per-element closure, since a shared observer has no per-element closure to capture it in.

//
// "Entered" means the threshold share of what CAN be visible is on screen: that share of the
// element, or - for an element taller than the viewport - that share of the viewport. A plain
// intersectionRatio threshold can never be reached by an element taller than viewport/threshold
// (e.g. a long Block Quote at 400% zoom), which would leave it stuck in its "leave" look forever.
// "Leave" is only set once the element is fully out of view, so an effect never resets while the
// user can still see it.
//
// The ratio at which a tall element meets its viewport-based condition depends on its height and
// the current zoom, so it can't be a fixed IntersectionObserver threshold. Instead the observer
// also fires at geometric steps (1, 0.8, 0.64, ... down to 0.1%): whatever that ratio turns out
// to be, a step lands within 25% above it, so the reveal fires at most shortly after the condition
// is met - for elements up to ~1000 viewports tall. Linear steps don't work here: a 5% step is
// never reached by an element more than 20 viewports tall.

const DEFAULT_THRESHOLD = 0.4;
const RATIO_STEPS = [0];
for (let step = 1; step >= 0.001; step *= 0.8) {
    RATIO_STEPS.push(step);
}

// A share between 0 and 1. Anything else (e.g. "40" meant as a percentage) would make the
// IntersectionObserver constructor throw and stop every later element from being observed, so
// it falls back to the default.
export function thresholdFor(root: HTMLElement): number {
    const raw = root.dataset.scrollRevealThreshold;
    const parsed = raw ? Number.parseFloat(raw) : NaN;
    if (parsed >= 0 && parsed <= 1) {
        return parsed;
    }
    if (raw) {
        console.warn(`data-scroll-reveal-threshold must be between 0 and 1, got "${raw}"`, root);
    }
    return DEFAULT_THRESHOLD;
}

function hasEntered(entry: IntersectionObserverEntry, threshold: number): boolean {
    const visibleHeight = entry.intersectionRect.height;
    const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
    const maxVisibleHeight = Math.min(entry.boundingClientRect.height, viewportHeight);
    return entry.isIntersecting && visibleHeight >= maxVisibleHeight * threshold;
}

function handleEntries(entries: IntersectionObserverEntry[], observer: IntersectionObserver, threshold: number): void {
    for (const entry of entries) {
        const root = entry.target as HTMLElement;
        const once = "scrollRevealOnce" in root.dataset;

        if (hasEntered(entry, threshold)) {
            root.dataset.scrollState = "entered";
            if (once) {
                observer.unobserve(root);
            }
        } else if (!entry.isIntersecting && !once) {
            root.dataset.scrollState = "leave";
        }
    }
}

function getOrCreateObserver(observersByThreshold: Map<number, IntersectionObserver>, threshold: number): IntersectionObserver {
    let observer = observersByThreshold.get(threshold);
    if (!observer) {
        observer = new IntersectionObserver((entries, self) => handleEntries(entries, self, threshold), {
            threshold: [...new Set([...RATIO_STEPS, threshold])].sort((a, b) => a - b),
        });
        observersByThreshold.set(threshold, observer);
    }
    return observer;
}

export function initScrollReveal(): void {
    const targets = document.querySelectorAll<HTMLElement>("[data-scroll-reveal]");

    if (!("IntersectionObserver" in window)) {
        // No observer support: nothing will ever move these off their server-rendered "leave"
        // state, so clear it outright and fall back to CSS's default (revealed) look instead -
        // a missing fade-in beats content that's permanently stuck muted.
        targets.forEach((root) => {
            delete root.dataset.scrollState;
        });
        return;
    }

    // Grouped by threshold since that's fixed per IntersectionObserver instance - in practice
    // almost every element shares DEFAULT_THRESHOLD, so this is one observer in the common case
    // and only splits further for elements that opt into a custom data-scroll-reveal-threshold.
    const observersByThreshold = new Map<number, IntersectionObserver>();

    whenShown(() => {
        // Two frames: the "leave" look (e.g. fade-in.ts's opacity 0, set from JS just before this)
        // has to be rendered once before an element turns "entered", or there is no starting point
        // to transition from and it just appears. Observing right away let the observer's first
        // callback land before that frame for everything already in view on load - in Edge the
        // text above the fold never faded in, only the Standard Hero (its own CSS animation) did.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            targets.forEach((root) => {
                getOrCreateObserver(observersByThreshold, thresholdFor(root)).observe(root);
            });
        }));
    });
}

// Runs `start` once the page is actually on screen. A page that's prerendered (Edge prerenders
// from the address bar far more readily than Chrome) or loaded in a background tab is laid out
// with a real viewport, so the observer would report everything above the fold as entered and
// play every reveal while nobody can see it - it then shows already revealed. Waiting keeps
// those elements in their "leave" look until the page is shown, so the reveal plays then.
function whenShown(start: () => void): void {
    const isShown = (): boolean => !(document as Document & { prerendering?: boolean }).prerendering && document.visibilityState === "visible";
    if (isShown()) {
        start();
        return;
    }

    let started = false;
    const check = (): void => {
        if (!started && isShown()) {
            started = true;
            document.removeEventListener("prerenderingchange", check);
            document.removeEventListener("visibilitychange", check);
            start();
        }
    };
    document.addEventListener("prerenderingchange", check);
    document.addEventListener("visibilitychange", check);
}
